import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { JsonType } from "posthog-js";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface StreakCelebrationMilestone {
  day: number;
  title: string;
  subtitle?: string;
}

export interface StreakCelebrationPayload {
  milestones: Array<StreakCelebrationMilestone>;
}

type ProjectPostCompletionStreakArgs = {
  currentStreak: number;
  lastListenDate: string;
  timezone: string;
  now?: Date | string;
};

type StreakToastSuppressionDayArgs = {
  timezone: string;
  now?: Date | string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isStreakCelebrationMilestone(value: unknown): value is StreakCelebrationMilestone {
  if (!isRecord(value)) {
    return false;
  }

  if (typeof value.day !== "number" || !Number.isInteger(value.day) || value.day < 1) {
    return false;
  }

  if (typeof value.title !== "string" || value.title.trim().length === 0) {
    return false;
  }

  if (value.subtitle !== undefined && typeof value.subtitle !== "string") {
    return false;
  }

  return true;
}

export function isValidStreakCelebrationPayload(
  payload: JsonType | undefined,
): payload is JsonType & StreakCelebrationPayload {
  if (!isRecord(payload) || !Array.isArray(payload.milestones) || payload.milestones.length === 0) {
    return false;
  }

  return payload.milestones.every(isStreakCelebrationMilestone);
}

export function hasConfiguredStreakMilestones(payload: JsonType | undefined): boolean {
  return isValidStreakCelebrationPayload(payload);
}

export function getStreakCelebrationMilestone(
  payload: JsonType | undefined,
  streakDay: number,
): StreakCelebrationMilestone | undefined {
  if (!isValidStreakCelebrationPayload(payload)) {
    return undefined;
  }

  return payload.milestones.find((milestone) => milestone.day === streakDay);
}

export function hasCrossedCompletionThreshold(
  previousPercentage: number | undefined,
  currentPercentage: number,
  thresholdPercentage: number,
): boolean {
  if (previousPercentage === undefined) {
    return false;
  }

  return previousPercentage < thresholdPercentage && currentPercentage >= thresholdPercentage;
}

export function projectPostCompletionStreak({
  currentStreak,
  lastListenDate,
  timezone,
  now,
}: ProjectPostCompletionStreakArgs): number {
  const currentDay = (now ? dayjs(now) : dayjs()).tz(timezone).startOf("day");
  const previousListenDay = dayjs(lastListenDate).tz(timezone).startOf("day");

  if (!previousListenDay.isValid()) {
    return Math.max(currentStreak, 1);
  }

  if (previousListenDay.isSame(currentDay, "day")) {
    return currentStreak;
  }

  if (previousListenDay.isSame(currentDay.subtract(1, "day"), "day")) {
    return currentStreak + 1;
  }

  return 1;
}

export function getStreakToastSuppressionDay({ timezone, now }: StreakToastSuppressionDayArgs): string {
  return (now ? dayjs(now) : dayjs()).tz(timezone).startOf("day").subtract(1, "minute").toISOString();
}
